import { countTerms, equivalent, evalNode, parseExpr, parseNumberAnswer, type Node } from './expr';
import { L } from '@/i18n';
import { gcd } from '@/core/util/misc';

/**
 * Validators check open "construct an example" / expression answers where
 * many different responses are correct.
 */
export type ValidatorResult = { ok: boolean; feedback: string };
export type Validator = (input: string, params: Record<string, number | string>) => ValidatorResult;

const isPrime = (n: number) => {
  if (n < 2 || !Number.isInteger(n)) return false;
  for (let i = 2; i * i <= n; i++) if (n % i === 0) return false;
  return true;
};

function parseFraction(s: string): { n: number; d: number } | null {
  const m = s.trim().replace(/[−–]/g, '-').match(/^(-?\d+)\s*\/\s*(\d+)$/);
  if (!m) return null;
  const n = Number(m[1]);
  const d = Number(m[2]);
  if (!d) return null;
  return { n, d };
}

function stripLhs(s: string, v: string): string {
  return s.replace(new RegExp(`^\\s*${v}\\s*=\\s*`, 'i'), '');
}

export const VALIDATORS: Record<string, Validator> = {
  'expr-equivalent': (input, p) => {
    const vars = String(p.vars ?? 'x').split(',');
    const raw = stripLhs(stripLhs(input, 'y'), 'f\\(x\\)').trim();
    if (!raw) return { ok: false, feedback: L('Write an expression.', 'Skriv et uttrykk.') };
    const eq = equivalent(raw, String(p.expr), vars);
    if (!eq) return { ok: false, feedback: L('That expression is not equivalent to the correct one. Check signs and coefficients.', 'Uttrykket er ikke likeverdig med det riktige. Sjekk fortegn og koeffisienter.') };
    const form = p.form as string | undefined;
    if (form === 'expanded' && /\(/.test(raw)) return { ok: false, feedback: L('Equivalent, but not expanded — remove the brackets.', 'Likeverdig, men ikke multiplisert ut — fjern parentesene.') };
    if (form === 'factored' && (raw.match(/\(/g) ?? []).length < 1) return { ok: false, feedback: L('Equivalent, but not factorised — write it as a product.', 'Likeverdig, men ikke faktorisert — skriv det som et produkt.') };
    if (form === 'simplified' && p.maxTerms && countTerms(raw) > Number(p.maxTerms)) return { ok: false, feedback: L('Equivalent, but not fully simplified — collect like terms.', 'Likeverdig, men ikke ferdig forenklet — trekk sammen like ledd.') };
    return { ok: true, feedback: L('Equivalent and in the requested form.', 'Likeverdig og på riktig form.') };
  },
  'fraction-simplest': (input, p) => {
    const f = parseFraction(input);
    if (!f) return { ok: false, feedback: L('Write the answer as a fraction a/b.', 'Skriv svaret som en brøk a/b.') };
    if (Math.abs(f.n / f.d - Number(p.n) / Number(p.d)) > 1e-9) return { ok: false, feedback: L('That fraction has a different value.', 'Den brøken har en annen verdi.') };
    if (gcd(f.n, f.d) !== 1) return { ok: false, feedback: L('Same value, but it can be simplified further.', 'Samme verdi, men den kan forkortes mer.') };
    return { ok: true, feedback: L('Fully simplified.', 'Ferdig forkortet.') };
  },
  'equivalent-fraction': (input, p) => {
    const f = parseFraction(input);
    if (!f) return { ok: false, feedback: L('Write a fraction a/b.', 'Skriv en brøk a/b.') };
    if (Math.abs(f.n / f.d - Number(p.n) / Number(p.d)) > 1e-9) return { ok: false, feedback: L('Not equivalent — multiply top and bottom by the same number.', 'Ikke likeverdig — gang teller og nevner med det samme tallet.') };
    if (p.minDen && f.d <= Number(p.minDen)) return { ok: false, feedback: L(`Equivalent, but the denominator must be greater than ${p.minDen}.`, `Likeverdig, men nevneren må være større enn ${p.minDen}.`) };
    return { ok: true, feedback: L('Correct — an equivalent fraction.', 'Riktig — en likeverdig brøk.') };
  },
  'fraction-between': (input, p) => {
    const v = parseNumberAnswer(input);
    if (v === null) return { ok: false, feedback: L('Write a fraction or decimal.', 'Skriv en brøk eller et desimaltall.') };
    const lo = Number(p.an) / Number(p.ad);
    const hi = Number(p.bn) / Number(p.bd);
    if (v > lo + 1e-12 && v < hi - 1e-12) return { ok: true, feedback: L('It lies strictly between them.', 'Det ligger mellom dem.') };
    return { ok: false, feedback: L(`Not strictly between ${p.an}/${p.ad} and ${p.bn}/${p.bd}.`, `Ikke mellom ${p.an}/${p.ad} og ${p.bn}/${p.bd}.`) };
  },
  'prime-factorisation': (input, p) => {
    try {
      const node = parseExpr(input.replace(/x/gi, '*'));
      const factorsOk = (nd: Node): boolean => {
        if (nd.k === 'num') return isPrime(nd.v);
        if (nd.k === 'bin' && nd.op === '*') return factorsOk(nd.a) && factorsOk(nd.b);
        if (nd.k === 'bin' && nd.op === '^') return nd.a.k === 'num' && isPrime(nd.a.v) && nd.b.k === 'num' && Number.isInteger(nd.b.v) && nd.b.v >= 1;
        return false;
      };
      const val = evalNode(node, {});
      if (Math.abs(val - Number(p.n)) > 1e-9) return { ok: false, feedback: L(`The product is ${val}, not ${p.n}.`, `Produktet er ${val}, ikke ${p.n}.`) };
      if (!factorsOk(node)) return { ok: false, feedback: L('The product is right, but not every factor is prime.', 'Produktet er riktig, men ikke alle faktorene er primtall.') };
      return { ok: true, feedback: L('Correct prime factorisation.', 'Riktig primtallsfaktorisering.') };
    } catch {
      return { ok: false, feedback: L('Write it like 2^2 × 3 × 5.', 'Skriv det slik: 2^2 × 3 × 5.') };
    }
  },
  'ratio-simplest': (input, p) => {
    const m = input.replace(/\s/g, '').match(/^(\d+)[:/](\d+)$/);
    if (!m) return { ok: false, feedback: L('Write the ratio as a : b.', 'Skriv forholdet som a : b.') };
    const a = Number(m[1]);
    const b = Number(m[2]);
    if (a * Number(p.b) !== b * Number(p.a)) return { ok: false, feedback: L('That ratio is not equivalent.', 'Det forholdet er ikke likeverdig.') };
    if (gcd(a, b) !== 1) return { ok: false, feedback: L('Equivalent, but not fully simplified.', 'Likeverdig, men ikke ferdig forkortet.') };
    return { ok: true, feedback: L('Fully simplified.', 'Ferdig forkortet.') };
  },
  point: (input, p) => {
    const m = input.replace(/[−–]/g, '-').replace(/\s/g, '').match(/^\(?(-?\d+(?:[.,]\d+)?)[,;](-?\d+(?:[.,]\d+)?)\)?$/);
    if (!m) return { ok: false, feedback: L('Write the point as (x, y).', 'Skriv punktet som (x, y).') };
    const x = Number(m[1].replace(',', '.'));
    const y = Number(m[2].replace(',', '.'));
    if (Math.abs(x - Number(p.x)) < 1e-9 && Math.abs(y - Number(p.y)) < 1e-9) return { ok: true, feedback: L('Correct point.', 'Riktig punkt.') };
    if (Math.abs(x - Number(p.y)) < 1e-9 && Math.abs(y - Number(p.x)) < 1e-9) return { ok: false, feedback: L('You swapped x and y — x comes first.', 'Du byttet om x og y — x kommer først.') };
    return { ok: false, feedback: L('Not the right point.', 'Ikke riktig punkt.') };
  },
  vector: (input, p) => {
    const m = input.replace(/[−–]/g, '-').replace(/\s/g, '').match(/^[[(]?(-?\d+(?:[.,]\d+)?)[,;](-?\d+(?:[.,]\d+)?)[\])]?$/);
    if (!m) return { ok: false, feedback: L('Write the vector as [x, y].', 'Skriv vektoren som [x, y].') };
    const x = Number(m[1].replace(',', '.'));
    const y = Number(m[2].replace(',', '.'));
    return Math.abs(x - Number(p.x)) < 1e-9 && Math.abs(y - Number(p.y)) < 1e-9 ? { ok: true, feedback: L('Correct vector.', 'Riktig vektor.') } : { ok: false, feedback: L('Check each component.', 'Sjekk hver komponent.') };
  },
  'linear-eq': (input, p) => {
    const raw = stripLhs(input, 'y').trim();
    if (!raw) return { ok: false, feedback: L('Write y = ax + b.', 'Skriv y = ax + b.') };
    if (equivalent(raw, `${p.a}x + ${p.b}`, ['x'])) return { ok: true, feedback: L('Correct line.', 'Riktig linje.') };
    return { ok: false, feedback: L('Check the slope and the intercept.', 'Sjekk stigningstallet og konstantleddet.') };
  },
  'linear-through': (input, p) => {
    const raw = stripLhs(input, 'y').trim();
    try {
      const n = parseExpr(raw);
      const f = (x: number) => evalNode(n, { x });
      const slope = f(1) - f(0);
      const linear = Math.abs(f(2) - 2 * f(1) + f(0)) < 1e-9 && Math.abs(f(5) - f(0) - 5 * slope) < 1e-9;
      if (!linear) return { ok: false, feedback: L('That is not a linear function.', 'Det er ikke en lineær funksjon.') };
      if (Math.abs(f(Number(p.x)) - Number(p.y)) > 1e-9) return { ok: false, feedback: L(`The line does not pass through (${p.x}, ${p.y}).`, `Linja går ikke gjennom (${p.x}, ${p.y}).`) };
      if (p.slopeSign && Math.sign(slope) !== Math.sign(Number(p.slopeSign))) return { ok: false, feedback: L('Passes through the point, but the slope has the wrong sign.', 'Går gjennom punktet, men stigningstallet har feil fortegn.') };
      return { ok: true, feedback: L('Valid line.', 'Gyldig linje.') };
    } catch {
      return { ok: false, feedback: L('Write it as y = ax + b.', 'Skriv det som y = ax + b.') };
    }
  },
  'equation-with-solution': (input, p) => {
    const parts = input.replace(/[−–]/g, '-').split('=');
    if (parts.length !== 2) return { ok: false, feedback: L('Write an equation with one "=".', 'Skriv en likning med ett «=».') };
    try {
      const lhs = parseExpr(parts[0]);
      const rhs = parseExpr(parts[1]);
      const g = (x: number) => evalNode(lhs, { x }) - evalNode(rhs, { x });
      const sol = Number(p.sol);
      if (Math.abs(g(sol)) > 1e-9) return { ok: false, feedback: L(`x = ${sol} does not satisfy your equation.`, `x = ${sol} passer ikke i likningen din.`) };
      const slope = g(1) - g(0);
      if (Math.abs(slope) < 1e-12) return { ok: false, feedback: L('Your equation is true for every x (or none) — it must have exactly one solution.', 'Likningen din stemmer for alle x (eller ingen) — den må ha nøyaktig én løsning.') };
      if (Math.abs(g(2) - 2 * g(1) + g(0)) > 1e-9) return { ok: false, feedback: L('Make it a linear equation.', 'Lag en lineær likning.') };
      if (p.bothSides) {
        const hasX = (s: string) => /x/i.test(s);
        if (!hasX(parts[0]) || !hasX(parts[1])) return { ok: false, feedback: L('Correct solution, but x must appear on both sides.', 'Riktig løsning, men x må stå på begge sider.') };
      }
      return { ok: true, feedback: L('Valid equation with the required solution.', 'Gyldig likning med riktig løsning.') };
    } catch {
      return { ok: false, feedback: L('Could not read the equation.', 'Kunne ikke lese likningen.') };
    }
  },
  dataset: (input, p) => {
    const nums = input.split(/[,;\s]+/).filter(Boolean).map((s) => Number(s.replace(',', '.')));
    if (nums.some((n) => !Number.isFinite(n) || !Number.isInteger(n))) return { ok: false, feedback: L('Use whole numbers separated by commas.', 'Bruk hele tall skilt med komma.') };
    if (nums.length !== Number(p.n)) return { ok: false, feedback: L(`Give exactly ${p.n} numbers.`, `Gi nøyaktig ${p.n} tall.`) };
    const mean = nums.reduce((a, b) => a + b, 0) / nums.length;
    const s = [...nums].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    const median = s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
    if (Math.abs(mean - Number(p.mean)) > 1e-9) return { ok: false, feedback: L(`Your mean is ${mean}, not ${p.mean}.`, `Gjennomsnittet ditt er ${mean}, ikke ${p.mean}.`) };
    if (p.median !== undefined && Math.abs(median - Number(p.median)) > 1e-9) return { ok: false, feedback: L(`Mean is right, but the median is ${median}, not ${p.median}.`, `Gjennomsnittet er riktig, men medianen er ${median}, ikke ${p.median}.`) };
    return { ok: true, feedback: L('Your data set meets every condition.', 'Datasettet ditt oppfyller alle kravene.') };
  },
};
